import { message } from "antd";
import { Product } from "../services/Product/index1";
import { localErrorHandler } from "../utils/errorHandler";
import fileServices from "../services/storage/index1";
import * as _ from "lodash";
import { addOnsService } from "../services/addOns";
import { getAddonDifference, isDeepEqual } from "../utils";
import { removeImageSizePrefix } from "../utils/image";

const namespace = "product";

const initialState = {
  list: [],
  pagination: {
    total: 0,
  },
  filters: {},
  currentProduct: null,
  loading: {
    fetch: false,
    add: false,
    update: false,
    delete: false,
    fetchById: false,
    updatePosition: false,
    loadMore: false,
  },
};

export const fetchProducts = (params) => ({
  type: `${namespace}/fetchProducts`,
  payload: params,
});

export const fetchMoreProducts = (params) => ({
  type: `${namespace}/fetchMoreProducts`,
  payload: params,
});

export const addProduct = (data) => ({
  type: `${namespace}/addProduct`,
  payload: data,
});

export const updateProduct = (productId, data) => ({
  type: `${namespace}/updateProduct`,
  payload: { productId, data },
});

export const deleteProduct = (productId) => ({
  type: `${namespace}/deleteProduct`,
  payload: productId,
});

export const fetchProductById = (productId) => ({
  type: `${namespace}/fetchProductById`,
  payload: productId,
});

export const updateProductPosition = (productId, data) => ({
  type: `${namespace}/updateProductPosition`,
  payload: { productId, data },
});

export const updateManyAddOns = (productId, data) => ({
  type: `${namespace}/updateManyAddOns`,
  payload: { productId, data },
});

export const deleteManyAddOns = (productId, data) => ({
  type: `${namespace}/deleteManyAddOns`,
  payload: { productId, data },
});

export default {
  namespace,
  state: initialState,
  effects: {
    *fetchProducts({ payload }, { call, put, select }) {
      yield put({ type: "startLoading", loadingType: "fetch" });
      const currentFilters = yield select((state) => state[namespace].filters);
      const shopId = yield select((state) => state.shop?.data?.id);
      const filtersToUse = payload || currentFilters;

      try {
        const response = yield call(Product.getProducts, {
          ...filtersToUse,
          shopId,
        });
        yield put({
          type: "setProducts",
          payload: {
            list: response.data.list || [],
            pagination: {
              total: response.data.total || 0,
            },
            filters: filtersToUse,
          },
        });
      } catch (error) {
        localErrorHandler({ namespace, error, stopLoading: "fetch" });
      } finally {
        yield put({ type: "stopLoading", loadingType: "fetch" });
      }
    },

    *fetchMoreProducts({ payload }, { call, put, select }) {
      yield put({ type: "startLoading", loadingType: "loadMore" });
      const currentFilters = yield select((state) => state[namespace].filters);
      const shopId = yield select((state) => state.shop?.data?.id);
      const filtersToUse = { ...currentFilters, ...payload };

      try {
        const response = yield call(Product.getProducts, {
          ...filtersToUse,
          shopId,
        });
        yield put({
          type: "updateProductsList",
          payload: {
            list: response.data.list || [],
            pagination: {
              total: response.data.total || 0,
            },
            filters: filtersToUse,
          },
        });
      } catch (error) {
        localErrorHandler({ namespace, error, stopLoading: "loadMore" });
      } finally {
        yield put({ type: "stopLoading", loadingType: "loadMore" });
      }
    },

    *addProduct({ payload }, { call, put }) {
      yield put({ type: "startLoading", loadingType: "add" });
      try {
        let updatedData = { ...payload };
        if (payload.image) {
          const { url } = yield call(fileServices.uploadFile, {
            file: payload.image,
            img_sizes: ["medium", "large"],
            model: "products",
            id: payload.id,
            img_field: "image",
          });

          updatedData = {
            ..._.omit(payload, ["image"]),
            image: url,
          };
        }

        yield call(Product.addProduct, updatedData);
        message.success("Product added successfully");
      } catch (error) {
        localErrorHandler({ namespace, error, stopLoading: "add" });
      } finally {
        yield put({ type: "stopLoading", loadingType: "add" });
      }
    },

    *updateProduct({ payload }, { call, put, select, all }) {
      const { productId, data } = payload;

      yield put({ type: "startLoading", loadingType: "update" });

      try {
        const currentProduct = yield select(
          (state) => state.product.currentProduct,
        );

        const currentAddons = currentProduct.addons || [];
        const formAddons = data?.addons || [];
        const addonsdifference = getAddonDifference(currentAddons, formAddons);

        const addOnTasks = [];

        if (addonsdifference?.toAdd?.length > 0) {
          addOnTasks.push(
            call(
              addOnsService.createManyAddOns,
              productId,
              addonsdifference.toAdd,
            ),
          );
        }

        if (addonsdifference?.toUpdate?.length > 0) {
          addOnTasks.push(
            call(
              addOnsService.updateManyAddOns,
              productId,
              addonsdifference.toUpdate,
            ),
          );
        }

        if (addonsdifference?.toRemove?.length > 0) {
          addOnTasks.push(
            call(addOnsService.deleteManyAddOns, productId, {
              addonIds: addonsdifference.toRemove,
            }),
          );
        }

        if (addOnTasks.length > 0) {
          yield all(addOnTasks);
        }

        let updatedData = { ...data };

        if (isDeepEqual(data.image, data.photoUrl)) {
          updatedData.image = removeImageSizePrefix(data.photoUrl) ?? "";
        } else {
          const { url } = yield call(fileServices.uploadFile, {
            file: data.image,
            img_sizes: ["medium", "large"],
            model: "products",
            id: payload.productId,
            img_field: "image",
          });
          updatedData.image = url;
          if (data.photoUrl) {
            yield call(
              fileServices.deleteFile,
              removeImageSizePrefix(data.photoUrl),
            );
          }
        }

        updatedData = _.omit(updatedData, [
          "photoUrl",
          "category_id",
          "addons",
        ]);

        yield call(Product.updateProduct, updatedData, productId);
        message.success("Product updated successfully");
      } catch (error) {
        yield call(localErrorHandler, {
          namespace,
          error,
          stopLoading: "update",
        });
      } finally {
        yield put({ type: "stopLoading", loadingType: "update" });
      }
    },

    *deleteProduct({ payload }, { call, put }) {
      yield put({ type: "startLoading", loadingType: "delete" });
      try {
        yield call(Product.deleteProduct, payload);
        message.success("Product deleted successfully");
        yield put({ type: "clearCurrentProductIfMatches", payload: payload });
        yield put({ type: "deleteProductFromList", payload });
      } catch (error) {
        localErrorHandler({ namespace, error, stopLoading: "delete" });
      } finally {
        yield put({ type: "stopLoading", loadingType: "delete" });
      }
    },

    *fetchProductById({ payload }, { call, put }) {
      yield put({ type: "startLoading", loadingType: "fetchById" });
      yield put({ type: "clearCurrentProduct" });
      try {
        const response = yield call(Product.getProductById, payload);
        yield put({ type: "setCurrentProduct", payload: response.data });
      } catch (error) {
        yield put({ type: "clearCurrentProduct" });
        localErrorHandler({ namespace, error, stopLoading: "fetchById" });
      } finally {
        yield put({ type: "stopLoading", loadingType: "fetchById" });
      }
    },

    *updateProductPosition({ payload }, { call, put }) {
      const { productId, data } = payload;
      yield put({ type: "startLoading", loadingType: "updatePosition" });
      try {
        yield call(Product.updateProductPosition, productId, data);
        message.success("Product position updated successfully");

        yield put({ type: "fetchProducts" });
      } catch (error) {
        localErrorHandler({ namespace, error, stopLoading: "updatePosition" });
      } finally {
        yield put({ type: "stopLoading", loadingType: "updatePosition" });
      }
    },

    *updateManyAddOns({ payload }, { call, put }) {
      try {
        const { productId, data } = payload;
        yield put({ type: "startLoading", loadingType: "update" });
        yield call(addOnsService.updateManyAddOns, productId, data);
        message.success("Product updated successfully");
      } catch (error) {
        localErrorHandler({ namespace, error, stopLoading: "update" });
      } finally {
        yield put({ type: "stopLoading", loadingType: "update" });
      }
    },
    *deleteManyAddOns({ payload }, { call, put }) {
      try {
        const { productId, data } = payload;
        debugger;
        yield put({ type: "startLoading", loadingType: "delete" });
        yield call(addOnsService.deleteManyAddOns, productId, data);
        message.success("Product updated successfully");
      } catch (error) {
        localErrorHandler({ namespace, error, stopLoading: "delete" });
      } finally {
        yield put({ type: "stopLoading", loadingType: "delete" });
      }
    },

    ///

    *updateProductDeliveryStatus({ payload }, { call, put }) {
      const { productId, data } = payload;

      try {
        let response;
        if (data.status === true) {
          response = yield call(
            Product.updateProductDeliveryStatusActivate,
            productId,
          );
        } else if (data.status === false) {
          response = yield call(
            Product.updateProducDeliveryStatusDeActivate,
            productId,
          );
        }
        if (response && response.data) {
          yield put({
            type: "updateProductInList",
            payload: { productId: productId, data: response.data },
          });

          yield put({
            type: "updateCurrentProductIfMatches",
            payload: { productId: productId, data: response.data },
          });
        }
        message.success("Product delivery status updated successfully");
      } catch (error) {
        localErrorHandler({ namespace, error });
      }
    },
    *updateProductPickupStatus({ payload }, { call, put }) {
      const { productId, data } = payload;

      try {
        let response;
        if (data.status === true) {
          response = yield call(
            Product.updateProductPickupStatusActivate,
            productId,
          );
        } else if (data.status === false) {
          response = yield call(
            Product.updateProductPickupStatusDeActivate,
            productId,
          );
        }
        if (response && response.data) {
          yield put({
            type: "updateProductInList",
            payload: { productId: productId, data: response.data },
          });

          yield put({
            type: "updateCurrentProductIfMatches",
            payload: { productId: productId, data: response.data },
          });
        }
        message.success("Product pickup status updated successfully");
      } catch (error) {
        localErrorHandler({ namespace, error });
      }
    },
  },

  reducers: {
    setProducts(state, { payload }) {
      return {
        ...state,
        list: payload.list,
        pagination: payload.pagination,
        filters: payload.filters,
      };
    },
    updateProductsList(state, { payload }) {
      return {
        ...state,
        list: [...state.list, ...payload.list],
        pagination: payload.pagination,
        filters: payload.filters,
      };
    },
    setCurrentProduct(state, { payload }) {
      return {
        ...state,
        currentProduct: payload,
      };
    },
    clearCurrentProduct(state) {
      return {
        ...state,
        currentProduct: null,
      };
    },
    deleteProductFromList(state, { payload }) {
      if (!payload) return state;
      const productList = state.list.filter(
        (product) => product.id !== payload,
      );
      const newTotal =
        state.pagination.total > 0 ? state.pagination.total - 1 : 0;
      return {
        ...state,
        list: productList,
        pagination: {
          ...state.pagination,
          total: newTotal,
        },
      };
    },
    clearCurrentProductIfMatches(state, { payload }) {
      if (state.currentProduct && state.currentProduct.id === payload) {
        return {
          ...state,
          currentProduct: null,
        };
      }
      return state;
    },
    updateCurrentProductIfMatches(state, { payload }) {
      if (
        state.currentProduct &&
        state.currentProduct.id === payload.productId
      ) {
        return {
          ...state,

          currentProduct: { ...state.currentProduct, ...payload.data },
        };
      }
      return state;
    },
    updateProductInList(state, { payload }) {
      const { productId, data } = payload;
      const productList = state.list.map((product) => {
        if (product.id === productId) {
          return { ...product, ...data };
        }
        return product;
      });
      return {
        ...state,
        list: productList,
      };
    },

    startLoading(state, { loadingType }) {
      return {
        ...state,
        loading: { ...state.loading, [loadingType]: true },
      };
    },
    stopLoading(state, { loadingType }) {
      return {
        ...state,
        loading: { ...state.loading, [loadingType]: false },
      };
    },
  },
};
